import React, { useState, useEffect } from 'react';
import { X, GripVertical, Trash2, Edit, Save, Type, AlignLeft, Image, Play, MousePointer, Settings, Plus, ChevronDown, Users, MessageSquare, Grid, Square, Layout } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest } from '../config/api';
import { buttonStyles } from '../utils/colors';
import { useToast } from '../context/ToastContext';
import { loadDynamicSections, getAllDynamicSections, getDynamicSection, renderDynamicSection } from '../utils/sectionRegistry';

// Helper function to get default style values
const getDefaultStyleValue = (style) => {
  const defaults = {
    fontSize: '16',
    fontWeight: 'normal',
    color: '#1f2937',
    backgroundColor: 'transparent',
    textAlign: 'center',
    borderRadius: '8',
    marginTop: '0',
    marginBottom: '16',
    paddingTop: '0',
    paddingBottom: '0'
  };
  
  return defaults[style] || '';
};

const SECTION_TYPES = {
  header: {
    label: 'Header',
    icon: Type,
    description: 'Large title text',
    defaultContent: {
      text: 'Thank You!',
      fontSize: '48',
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#1f2937',
      sectionBackgroundColor: 'transparent',
      marginBottom: '24'
    }
  },
  paragraph: {
    label: 'Paragraph',
    icon: AlignLeft,
    description: 'Body text content',
    defaultContent: {
      text: 'Your response has been recorded successfully.',
      fontSize: '18',
      fontWeight: 'normal',
      textAlign: 'center',
      color: '#6b7280',
      sectionBackgroundColor: 'transparent',
      marginBottom: '16'
    }
  },
  image: {
    label: 'Image',
    icon: Image,
    description: 'Display an image',
    defaultContent: {
      src: '',
      alt: '',
      width: '300',
      height: 'auto',
      borderRadius: '8',
      textAlign: 'center',
      sectionBackgroundColor: 'transparent',
      marginBottom: '24'
    }
  },
  video: {
    label: 'Video',
    icon: Play,
    description: 'Embed a video',
    defaultContent: {
      src: '',
      width: '560',
      height: '315',
      autoplay: false,
      controls: true,
      textAlign: 'center',
      sectionBackgroundColor: 'transparent',
      marginBottom: '24'
    }
  },
  cta: {
    label: 'Button',
    icon: MousePointer,
    description: 'Button or link',
    defaultContent: {
      text: 'Continue',
      url: '',
      backgroundColor: '#4f46e5',
      textColor: '#ffffff',
      borderRadius: '8',
      padding: '12 24',
      fontSize: '16',
      fontWeight: 'medium',
      buttonSize: 'medium', // small, medium, large
      buttonWidth: 'auto', // auto, full
      textAlign: 'center',
      sectionBackgroundColor: 'transparent',
      marginBottom: '16'
    }
  }
};

// Helper function to get icon component from string name
const getIconComponent = (iconName) => {
  const iconMap = {
    Type, AlignLeft, Image, Play, MousePointer, Settings, Plus, ChevronDown,
    Users, MessageSquare, Grid, Square, Layout, Edit, Trash2, GripVertical, X, Save
  };
  return iconMap[iconName] || Square; // Default to Square if icon not found
};

// Sortable section component
function SortableSection({ section, onEdit, onDelete, onSelect, onQuickUpdate, isSelected = false, isPreview = false, allSectionTypes, renderDynamicSection, selectedColumn, setSelectedColumn }) {
  const [isEditingText, setIsEditingText] = useState(false);
  const [editText, setEditText] = useState('');
  const [isDraggingMargin, setIsDraggingMargin] = useState(null);
  const [dragStartY, setDragStartY] = useState(0);
  const [localContent, setLocalContent] = useState(section.content); // Local state for real-time updates
  
  // Dynamic section inline editing state
  const [editingField, setEditingField] = useState(null);
  const [dynamicEditText, setDynamicEditText] = useState('');

  // Sync local content with prop changes (but only when not dragging)
  useEffect(() => {
    if (!isDraggingMargin) {
      setLocalContent(section.content);
    }
  }, [section.content, isDraggingMargin, section.id]);

  // Also sync when section changes completely (different section selected)
  useEffect(() => {
    setLocalContent(section.content);
  }, [section.id, section.content]);

  // Handle inline text editing
  const handleDoubleClick = (e) => {
    if (isPreview && ['header', 'paragraph', 'cta'].includes(section.section_type)) {
      e.stopPropagation();
      setEditText(localContent.text || '');
      setIsEditingText(true);
    }
  };

  const handleTextSubmit = () => {
    if (onQuickUpdate && editText !== localContent.text) {
      onQuickUpdate(section.id, { text: editText });
    }
    setIsEditingText(false);
  };

  const handleTextKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    } else if (e.key === 'Escape') {
      setIsEditingText(false);
    }
  };

  // Handle margin dragging
  const handleMarginPointerDown = (type, e) => {
    if (!isPreview) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Capture the pointer for consistent tracking
    e.currentTarget.setPointerCapture(e.pointerId);
    
    setIsDraggingMargin(type);
    setDragStartY(e.clientY);
    
    // Get the most reliable current value by checking multiple sources
    let currentValue = 0;
    
    // Method 1: Try local content first
    const localValue = parseInt(localContent[type] || 0);
    
    // Method 2: Try section content as backup
    const sectionValue = parseInt(section.content[type] || 0);
    
    // Method 3: Try computed style from DOM as last resort
    const sectionElement = e.currentTarget.closest('.section-hover-parent');
    const contentElement = sectionElement?.querySelector('h1, p, img, iframe, button');
    let computedValue = 0;
    
    if (contentElement) {
      const computedStyle = window.getComputedStyle(contentElement);
      const computedMargin = computedStyle[type === 'marginTop' ? 'marginTop' : 'marginBottom'];
      computedValue = parseInt(computedMargin) || 0;
    }
    
    // Use the most reliable value (prefer non-zero values)
    if (localValue > 0) {
      currentValue = localValue;
    } else if (sectionValue > 0) {
      currentValue = sectionValue;
    } else if (computedValue > 0) {
      currentValue = computedValue;
    } else {
      // All are zero or undefined, use localValue as fallback
      currentValue = localValue;
    }
    
    console.log(`Drag start for ${type}: local=${localValue}, section=${sectionValue}, computed=${computedValue}, using=${currentValue}`);
    
    // Keep track of the current drag value for API updates
    let currentDragValue = currentValue;
    let isDragging = true;
    let firstMoveEvent = true; // Track if this is the first move event
    
    // Store the start value locally to avoid state timing issues
    const startValue = currentValue;
    
    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.WebkitUserSelect = 'none';
    document.body.style.MozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    
    const handlePointerMove = (e) => {
      if (!isDragging) return; // Prevent any late events
      e.preventDefault();
      const deltaY = e.clientY - dragStartY;

      console.log(`Drag move ${type}: startValue=${startValue}, delta=${deltaY}`);

      
      // Skip the first move event if it has an unreasonably large delta (>50px)
      // This prevents the initial jump issue on page refresh
      if (firstMoveEvent && Math.abs(deltaY) > 50) {
        console.log(`Skipping first move event with large delta: ${deltaY}px - resetting drag start position`);
        firstMoveEvent = false;
        // Reset the drag start position to current position to fix subsequent events
        setDragStartY(e.clientY);
        return;
      }
      firstMoveEvent = false;

      // Apply a sensitivity factor to make dragging more controlled
      // Divide by 2 to make it less sensitive (user needs to drag 2px to change 1px)
      const scaledDelta = Math.round(deltaY / 2);
      const newValue = Math.max(0, Math.min(500, startValue + scaledDelta));
      
      console.log(`Drag move ${type}: startValue=${startValue}, deltaY=${deltaY}, scaledDelta=${scaledDelta}, newValue=${newValue}`);
      
      currentDragValue = newValue;
      
      // Update local state immediately for real-time visual feedback
      // Use functional update to avoid stale closures
      setLocalContent(prev => ({ ...prev, [type]: newValue.toString() }));
    };

    const handlePointerUp = (e) => {
      isDragging = false; // Stop processing move events
      
      // Make final API call with the exact final value
      if (onQuickUpdate && currentDragValue !== currentValue) {
        onQuickUpdate(section.id, { [type]: currentDragValue.toString() });
      }
      
      // Clear dragging state after a brief delay to prevent state conflicts
      setTimeout(() => {
        setIsDraggingMargin(null);
      }, 50);
      
      // Release pointer capture
      try {
        if (e.target && e.target.releasePointerCapture) {
          e.target.releasePointerCapture(e.pointerId);
        }
      } catch (err) {
        // Ignore pointer capture errors
      }
      
      // Restore text selection and cursor
      document.body.style.userSelect = '';
      document.body.style.WebkitUserSelect = '';
      document.body.style.MozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.cursor = '';
      
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove, { passive: false });
    document.addEventListener('pointerup', handlePointerUp, { passive: true });
    document.addEventListener('pointercancel', handlePointerUp, { passive: true });
  };

  // Render text with inline editing
  const renderTextContent = () => {
    if (!localContent.text) return null;
    
    if (isEditingText) {
      return (
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleTextSubmit}
          onKeyDown={handleTextKeyPress}
          className="w-full bg-transparent border-2 border-blue-500 rounded px-2 py-1 outline-none"
          autoFocus
          style={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
        />
      );
    }

    return (
      <span 
        onDoubleClick={handleDoubleClick}
        className={isPreview ? 'cursor-text hover:bg-blue-50 hover:bg-opacity-50 px-1 py-0.5 rounded' : ''}
        title={isPreview ? 'Double-click to edit' : ''}
      >
        {localContent.text}
      </span>
    );
  };

  // Render margin dragger
  const renderMarginDragger = (type) => {
    if (!isPreview) return null;
    
    const isTop = type === 'marginTop';
    const value = parseInt(localContent[type] || 0);
    
    return (
      <div 
        className={`absolute left-1/2 transform -translate-x-1/2 ${
          isTop ? '-top-3' : '-bottom-3'
        } flex items-center z-20 margin-dragger`}
        style={{ pointerEvents: 'auto' }}
      >
        <div 
          className={`w-20 h-6 bg-blue-500 rounded cursor-ns-resize transition-opacity duration-200 flex items-center justify-center dragger-handle ${
            isDraggingMargin === type ? 'opacity-100 bg-blue-600' : 'opacity-0'
          }`}
          onPointerDown={(e) => handleMarginPointerDown(type, e)}
          title={`Drag to adjust ${isTop ? 'top' : 'bottom'} margin`}
          style={{ 
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            touchAction: 'none' // Prevent default touch behaviors
          }}
        >
          <span className="text-xs text-white font-medium pointer-events-none">{value}px</span>
        </div>
      </div>
    );
  };
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Compact list view for builder panel
  const renderCompactView = () => {
    const content = section.content;
    
    // Get section type info with multiple fallbacks
    let sectionTypeInfo;
    if (allSectionTypes && allSectionTypes[section.section_type]) {
      sectionTypeInfo = allSectionTypes[section.section_type];
    } else if (SECTION_TYPES && SECTION_TYPES[section.section_type]) {
      sectionTypeInfo = SECTION_TYPES[section.section_type];
    } else {
      sectionTypeInfo = { 
        label: section.section_type || 'Unknown Section', 
        icon: Square 
      };
    }
    
    let preview = '';
    switch (section.section_type) {
      case 'header':
      case 'paragraph':
        preview = content.text ? content.text.substring(0, 50) + (content.text.length > 50 ? '...' : '') : 'No text';
        break;
      case 'image':
        preview = content.src ? 'Image: ' + content.alt || 'Untitled image' : 'No image selected';
        break;
      case 'video':
        preview = content.src ? 'Video embedded' : 'No video URL';
        break;
      case 'cta':
        preview = content.text || 'Button';
        break;
      default:
        // For dynamic sections, try to show meaningful preview
        if (allSectionTypes && allSectionTypes[section.section_type] && allSectionTypes[section.section_type].isDynamic) {
          if (content.columns && content.columns.length > 0) {
            const firstColumn = content.columns[0];
            preview = firstColumn.title || firstColumn.text || 'Dynamic section content';
          } else {
            preview = allSectionTypes[section.section_type].description || 'Dynamic section';
          }
        } else {
          preview = 'Section content';
        }
    }

    return (
      <div className="flex items-center space-x-2 p-2">
        <div className="flex-shrink-0">
          {sectionTypeInfo?.icon && React.createElement(
            typeof sectionTypeInfo.icon === 'string' ? getIconComponent(sectionTypeInfo.icon) : sectionTypeInfo.icon, 
            { className: "w-3 h-3 text-gray-500" }
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-900 truncate">
              {sectionTypeInfo?.label || section.section_type}
            </p>
          </div>
          <p className="text-xs text-gray-500 truncate">
            {preview}
          </p>
        </div>
      </div>
    );
  };

  const renderSectionPreview = () => {
    const content = localContent; // Use localContent for real-time updates
    
    // Helper function to create spacing styles for content elements (NO margins)
    const getContentStyles = (baseStyles) => {
      const {
        margin,
        marginTop: baseMarginTop,
        marginBottom: baseMarginBottom,
        marginLeft: baseMarginLeft,
        marginRight: baseMarginRight,
        ...otherBaseStyles
      } = baseStyles;
      
      return {
        ...otherBaseStyles,
        margin: 0, // Reset all margins on content elements
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        paddingTop: `${content.paddingTop || 0}px`,
        paddingBottom: `${content.paddingBottom || 0}px`,
        paddingLeft: `${content.paddingLeft || 0}px`,
        paddingRight: `${content.paddingRight || 0}px`,
      };
    };

    // Helper function to create section wrapper styles (WITH margins for spacing between sections)
    const getSectionWrapperStyles = () => ({
      backgroundColor: content.sectionBackgroundColor && content.sectionBackgroundColor !== 'transparent' ? content.sectionBackgroundColor : 'transparent',
      borderRadius: content.borderRadius ? `${content.borderRadius}px` : '0px',
      textAlign: content.textAlign || 'center',
      padding: (content.sectionBackgroundColor && content.sectionBackgroundColor !== 'transparent') ? '16px' : '0px',
      // Apply section margins here for spacing between sections
      marginTop: `${content.marginTop || 0}px`,
      marginBottom: `${content.marginBottom || 24}px`,
      marginLeft: `${content.marginLeft || 0}px`,
      marginRight: `${content.marginRight || 0}px`,
    });
    
    const sectionWrapper = (children) => (
      <div 
        className="relative section-hover-parent" 
        style={{
          ...getSectionWrapperStyles(),
          position: 'relative',
          zIndex: 1
        }}
      >
        {renderMarginDragger('marginTop')}
        {children}
        {renderMarginDragger('marginBottom')}
      </div>
    );
    
    switch (section.section_type) {
      case 'header':
        return sectionWrapper(
          <h1 style={getContentStyles({
            fontSize: `${content.fontSize || 48}px`,
            fontWeight: content.fontWeight || 'bold',
            textAlign: 'inherit',
            color: content.color || '#1f2937',
          })}>
            {renderTextContent()}
          </h1>
        );
      
      case 'paragraph':
        return sectionWrapper(
          <p style={getContentStyles({
            fontSize: `${content.fontSize || 18}px`,
            fontWeight: content.fontWeight || 'normal',
            textAlign: 'inherit',
            color: content.color || '#6b7280',
            lineHeight: 1.6,
          })}>
            {renderTextContent()}
          </p>
        );
      
      case 'image':
        return content.src ? sectionWrapper(
          <img
            src={content.src}
            alt={content.alt || ''}
            style={getContentStyles({
              width: content.width === 'auto' ? 'auto' : `${content.width}px`,
              height: content.height === 'auto' ? 'auto' : `${content.height}px`,
              borderRadius: `${content.borderRadius || 8}px`,
              maxWidth: '100%',
              display: 'block',
              margin: '0 auto',
            })}
          />
        ) : sectionWrapper(
          <div 
            style={getContentStyles({
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              color: '#6b7280',
            })}
          >
            <Image className="w-12 h-12 mx-auto mb-2" />
            <p>No image selected</p>
          </div>
        );
      
      case 'video':
        return content.src ? sectionWrapper(
          <iframe
            src={content.src}
            width={content.width || 560}
            height={content.height || 315}
            style={getContentStyles({
              borderRadius: `${content.borderRadius || 8}px`,
              maxWidth: '100%',
              display: 'block',
              margin: '0 auto',
            })}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Thank you page video"
          />
        ) : sectionWrapper(
          <div 
            style={getContentStyles({
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              color: '#6b7280',
            })}
          >
            <Play className="w-12 h-12 mx-auto mb-2" />
            <p>No video URL provided</p>
          </div>
        );
      
      case 'cta':
        // Helper function to get button size styles
        const getButtonSizeStyles = () => {
          const buttonSize = content.buttonSize || 'medium';
          const sizeMap = {
            small: { fontSize: '14px', padding: '8px 16px' },
            medium: { fontSize: '16px', padding: '12px 24px' },
            large: { fontSize: '18px', padding: '16px 32px' }
          };
          return sizeMap[buttonSize] || sizeMap.medium;
        };

        const getButtonWidthStyles = () => {
          const buttonWidth = content.buttonWidth || 'auto';
          if (buttonWidth === 'full') {
            return {
              width: '100%',
              display: 'block'
            };
          } else if (buttonWidth === 'custom') {
            return {
              width: content.customButtonWidth || '200px',
              display: 'inline-block'
            };
          } else {
            return {
              width: 'auto',
              display: 'inline-block'
            };
          }
        };

        const buttonSizeStyles = getButtonSizeStyles();
        const buttonWidthStyles = getButtonWidthStyles();

        return sectionWrapper(
          <div style={getContentStyles({})}>
            <button
              style={{
                backgroundColor: content.backgroundColor || '#4f46e5',
                color: content.textColor || '#ffffff',
                borderRadius: `${content.borderRadius || 8}px`,
                padding: content.padding ? content.padding.split(' ').map(p => `${p}px`).join(' ') : buttonSizeStyles.padding,
                fontSize: content.fontSize ? `${content.fontSize}px` : buttonSizeStyles.fontSize,
                fontWeight: content.fontWeight || 'medium',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'none',
                ...buttonWidthStyles
              }}
              onClick={(e) => {
                if (content.url && !isPreview) {
                  e.preventDefault();
                }
              }}
            >
              {renderTextContent()}
            </button>
          </div>
        );
      
      default:
        // Check if this is a dynamic section and render it
        if (allSectionTypes && allSectionTypes[section.section_type] && allSectionTypes[section.section_type].isDynamic && renderDynamicSection) {
          try {
            // Create editing state object for dynamic sections
            const editingState = isPreview ? {
              editingField,
              setEditingField,
              editText: dynamicEditText,
              setEditText: setDynamicEditText,
              selectedColumn: typeof setSelectedColumn !== 'undefined' ? selectedColumn : undefined,
              setSelectedColumn: typeof setSelectedColumn !== 'undefined' ? 
                (columnIndex) => setSelectedColumn({ sectionId: section.id, columnIndex }) : undefined,
              sectionId: section.id,
              onSelectSection: onSelect // Add section selection function
            } : null;

            // Create a custom quick update function for dynamic sections
            const dynamicQuickUpdate = (key, value) => {
              if (onQuickUpdate) {
                onQuickUpdate(section.id, { [key]: value });
              }
            };

            const rendered = renderDynamicSection(section.section_type, content, getContentStyles, editingState, dynamicQuickUpdate);
            
            if (rendered) {
              return sectionWrapper(rendered);
            }
          } catch (error) {
            console.error('Error rendering dynamic section:', error);
          }
        }
        
        return <div className="text-gray-500">Unknown section type: {section.section_type}</div>;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative cursor-pointer transition-all ${
        !isPreview 
          ? `bg-white border rounded-lg mb-2 ${isSelected ? 'border-indigo-500 shadow-md bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}` 
          : `mb-4 ${isSelected ? 'ring-2 ring-indigo-500 ring-opacity-50 rounded-lg' : ''}`
      }`}
      onClick={() => onSelect && onSelect()}
    >
      {!isPreview ? (
        // Compact list view for builder panel
        <>
          <div className="absolute top-2 right-2 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(section);
              }}
              className="p-1 text-gray-400 hover:text-indigo-600 rounded"
            >
              <Edit className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(section.id);
              }}
              className="p-1 text-gray-400 hover:text-red-600 rounded"
            >
              <Trash2 className="w-3 h-3" />
            </button>
            <div
              {...attributes}
              {...listeners}
              className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-3 h-3" />
            </div>
          </div>
          {renderCompactView()}
        </>
      ) : (
        // Full preview for preview panel
        <>
          <div className="pr-4">
            {renderSectionPreview()}
          </div>
        </>
      )}
    </div>
  );
}

// Section editor modal
function SectionEditor({ section, sectionType, onSave, onCancel, allSectionTypes }) {
  const [content, setContent] = useState(
    section ? { ...section.content } : { 
      ...((allSectionTypes && allSectionTypes[sectionType]?.defaultContent) || SECTION_TYPES[sectionType]?.defaultContent || {})
    }
  );

  // Initialize dynamic sections with proper column structure
  useEffect(() => {
    if (allSectionTypes && allSectionTypes[sectionType]?.isDynamic) {
      const dynamicSection = getDynamicSection(sectionType);
      if (dynamicSection && dynamicSection.settings && dynamicSection.settings.columnStructure) {
        const testimonialCount = content.testimonialCount || 3;
        const currentTestimonials = content.actualColumns || [];
        
        // If we need more testimonials than we have, add them
        if (currentTestimonials.length < testimonialCount) {
          const defaultTestimonials = [...currentTestimonials];
          
          for (let i = currentTestimonials.length; i < testimonialCount; i++) {
            const testimonial = {
              id: `testimonial-${Date.now()}-${i}`
            };
            
            // Add default values for each field in the column structure
            dynamicSection.settings.columnStructure.fields.forEach(field => {
              testimonial[field.key] = field.default || '';
              
              // Add style defaults based on possible_styles
              if (field.possible_styles) {
                field.possible_styles.forEach(style => {
                  testimonial[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                });
              }
            });
            
            defaultTestimonials.push(testimonial);
          }
          
          setContent(prev => ({ ...prev, actualColumns: defaultTestimonials }));
        }
        // If we have more testimonials than needed, trim them
        else if (currentTestimonials.length > testimonialCount) {
          const trimmedTestimonials = currentTestimonials.slice(0, testimonialCount);
          setContent(prev => ({ ...prev, actualColumns: trimmedTestimonials }));
        }
      }
    }
  }, [section, sectionType, allSectionTypes, content.testimonialCount, content.actualColumns]);

  const updateContent = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave({
      ...section,
      section_type: sectionType,
      content
    });
  };

  // Common styling fields component
  const renderSpacingControls = () => (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-medium text-gray-900">Spacing</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Margin Top (px)</label>
          <input
            type="number"
            value={content.marginTop || '0'}
            onChange={(e) => updateContent('marginTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Margin Bottom (px)</label>
          <input
            type="number"
            value={content.marginBottom || ''}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="500"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Top (px)</label>
          <input
            type="number"
            value={content.paddingTop || '0'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Bottom (px)</label>
          <input
            type="number"
            value={content.paddingBottom || '0'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Left (px)</label>
          <input
            type="number"
            value={content.paddingLeft || '0'}
            onChange={(e) => updateContent('paddingLeft', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Padding Right (px)</label>
          <input
            type="number"
            value={content.paddingRight || '0'}
            onChange={(e) => updateContent('paddingRight', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min="0"
            max="100"
          />
        </div>
      </div>
    </div>
  );

  // Common styling controls
  const renderStyleControls = () => (
    <div className="space-y-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-medium text-gray-900">Style & Alignment</h4>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Text Align</label>
          <select
            value={content.textAlign || 'center'}
            onChange={(e) => updateContent('textAlign', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={content.sectionBackgroundColor === 'transparent' ? '#ffffff' : (content.sectionBackgroundColor || '#ffffff')}
              onChange={(e) => updateContent('sectionBackgroundColor', e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
            />
            <button
              onClick={() => updateContent('sectionBackgroundColor', 'transparent')}
              className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Section background color</p>
        </div>
      </div>
    </div>
  );

  const renderEditor = () => {
    switch (sectionType) {
      case 'header':
      case 'paragraph':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text</label>
              <textarea
                value={content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={sectionType === 'header' ? 2 : 4}
                placeholder="Enter text content"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Size (px)</label>
                <input
                  type="number"
                  value={content.fontSize || ''}
                  onChange={(e) => updateContent('fontSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  min="8"
                  max="120"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Weight</label>
                <select
                  value={content.fontWeight || 'normal'}
                  onChange={(e) => updateContent('fontWeight', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="normal">Normal</option>
                  <option value="medium">Medium</option>
                  <option value="semibold">Semibold</option>
                  <option value="bold">Bold</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
              <input
                type="color"
                value={content.color || '#1f2937'}
                onChange={(e) => updateContent('color', e.target.value)}
                className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
              />
            </div>
            {renderStyleControls()}
            {renderSpacingControls()}
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
              <input
                type="url"
                value={content.src || ''}
                onChange={(e) => updateContent('src', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
              <input
                type="text"
                value={content.alt || ''}
                onChange={(e) => updateContent('alt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Description of the image"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Width (px or 'auto')</label>
                <input
                  type="text"
                  value={content.width || ''}
                  onChange={(e) => updateContent('width', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="300 or auto"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Height (px or 'auto')</label>
                <input
                  type="text"
                  value={content.height || ''}
                  onChange={(e) => updateContent('height', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="200 or auto"
                />
              </div>
            </div>
            {renderStyleControls()}
            {renderSpacingControls()}
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Embed URL</label>
              <input
                type="url"
                value={content.src || ''}
                onChange={(e) => updateContent('src', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://www.youtube.com/embed/VIDEO_ID"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use YouTube embed URLs: https://www.youtube.com/embed/VIDEO_ID
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Width (px)</label>
                <input
                  type="number"
                  value={content.width || ''}
                  onChange={(e) => updateContent('width', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="560"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Height (px)</label>
                <input
                  type="number"
                  value={content.height || ''}
                  onChange={(e) => updateContent('height', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="315"
                />
              </div>
            </div>
            {renderStyleControls()}
            {renderSpacingControls()}
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
              <input
                type="text"
                value={content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Continue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Link URL (optional)</label>
              <input
                type="url"
                value={content.url || ''}
                onChange={(e) => updateContent('url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://example.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Size</label>
                <select
                  value={content.buttonSize || 'medium'}
                  onChange={(e) => updateContent('buttonSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Element Width</label>
                <select
                  value={content.buttonWidth || 'auto'}
                  onChange={(e) => updateContent('buttonWidth', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="auto">Auto (fit content)</option>
                  <option value="full">Full Width</option>
                  <option value="custom">Custom</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Controls the button element only</p>
              </div>
            </div>
            {content.buttonWidth === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Custom Button Width</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={content.customButtonWidth || '200px'}
                    onChange={(e) => updateContent('customButtonWidth', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="200px or 50%"
                  />
                  <div className="text-xs text-gray-500">
                    Use px or %
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Background Color</label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={content.sectionBackgroundColor === 'transparent' ? '#ffffff' : (content.sectionBackgroundColor || '#ffffff')}
                  onChange={(e) => updateContent('sectionBackgroundColor', e.target.value)}
                  className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                />
                <input
                  type="text"
                  value={content.sectionBackgroundColor || 'transparent'}
                  onChange={(e) => updateContent('sectionBackgroundColor', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="transparent"
                />
                <button
                  onClick={() => updateContent('sectionBackgroundColor', 'transparent')}
                  className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Background behind the entire button section</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Background</label>
                <input
                  type="color"
                  value={content.backgroundColor || '#4f46e5'}
                  onChange={(e) => updateContent('backgroundColor', e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button Text Color</label>
                <input
                  type="color"
                  value={content.textColor || '#ffffff'}
                  onChange={(e) => updateContent('textColor', e.target.value)}
                  className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                />
              </div>
            </div>
            {renderStyleControls()}
            {renderSpacingControls()}
          </div>
        );

      default:
        // Check if this is a dynamic section
        const dynamicSection = getDynamicSection(sectionType);
        if (dynamicSection && dynamicSection.settings) {
          return renderDynamicSectionEditor(dynamicSection.settings);
        }
        
        return <div>Unknown section type</div>;
    }
  };

  // Render editor for dynamic sections
  const renderDynamicSectionEditor = (settings) => {
    return (
      <div className="space-y-6">
        {/* Render fields if any */}
        {settings.fields && settings.fields.map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.label}
            </label>
            {field.type === 'select' ? (
              <select
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {field.options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'number' ? (
              <input
                type="number"
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                min={field.min}
                max={field.max}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            ) : (
              <input
                type="text"
                value={content[field.key] || field.default}
                onChange={(e) => updateContent(field.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            )}
          </div>
        ))}

        {/* Render column structure if any */}
        {settings.columnStructure && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Column Content</h4>
            <div className="space-y-4">
              {content.actualColumns?.map((column, index) => (
                <div key={column.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      Column {index + 1}
                    </h5>
                    {content.actualColumns.length > 1 && (
                      <button
                        onClick={() => {
                          const newColumns = content.actualColumns.filter((_, i) => i !== index);
                          updateContent('actualColumns', newColumns);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Column fields based on structure */}
                  <div className="space-y-3">
                    {settings.columnStructure.fields.map(field => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {field.label}
                        </label>
                        {field.type === 'textarea' ? (
                          <textarea
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || field.default}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : field.type === 'color' ? (
                          <input
                            type="color"
                            value={column[field.key] || field.default || '#ffffff'}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            className="w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                          />
                        ) : field.type === 'icon' ? (
                          <input
                            type="text"
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || 'Icon name (e.g., Heart, Star)'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <input
                            type="text"
                            value={column[field.key] || ''}
                            onChange={(e) => {
                              const newColumns = [...content.actualColumns];
                              newColumns[index] = { ...column, [field.key]: e.target.value };
                              updateContent('actualColumns', newColumns);
                            }}
                            placeholder={field.placeholder || field.default}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        )}
                        
                        {/* Field style controls */}
                        {field.possible_styles && field.possible_styles.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                            {field.possible_styles.includes('fontSize') && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
                                <input
                                  type="number"
                                  value={column[`${field.key}FontSize`] || '16'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}FontSize`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                  min="8"
                                  max="72"
                                />
                              </div>
                            )}
                            {field.possible_styles.includes('fontWeight') && (
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Weight</label>
                                <select
                                  value={column[`${field.key}FontWeight`] || 'normal'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}FontWeight`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                >
                                  <option value="normal">Normal</option>
                                  <option value="medium">Medium</option>
                                  <option value="semibold">Semibold</option>
                                  <option value="bold">Bold</option>
                                </select>
                              </div>
                            )}
                            {field.possible_styles.includes('color') && (
                              <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                                <input
                                  type="color"
                                  value={column[`${field.key}Color`] || '#1f2937'}
                                  onChange={(e) => {
                                    const newColumns = [...content.actualColumns];
                                    newColumns[index] = { ...column, [`${field.key}Color`]: e.target.value };
                                    updateContent('actualColumns', newColumns);
                                  }}
                                  className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )) || null}
              
              {/* Add column button */}
              {content.actualColumns && content.actualColumns.length < (settings.columnStructure.maxColumns || 3) && (
                <button
                  onClick={() => {
                    const newColumn = {
                      id: `column-${Date.now()}`
                    };
                    
                    // Add default values for each field in the column structure
                    settings.columnStructure.fields.forEach(field => {
                      newColumn[field.key] = field.default || '';
                      
                      // Add style defaults based on possible_styles
                      if (field.possible_styles) {
                        field.possible_styles.forEach(style => {
                          newColumn[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                        });
                      }
                    });
                    
                    updateContent('actualColumns', [...content.actualColumns, newColumn]);
                  }}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600"
                >
                  + Add Column
                </button>
              )}
            </div>
          </div>
        )}

        {/* Render legacy columns if any */}
        {settings.columns && !settings.columnStructure && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Content Columns</h4>
            <div className="space-y-4">
              {content.columns?.map((column, index) => (
                <div key={column.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      {settings.columns[index]?.label || `Column ${index + 1}`}
                    </h5>
                    {content.columns.length > 1 && (
                      <button
                        onClick={() => {
                          const newColumns = content.columns.filter((_, i) => i !== index);
                          updateContent('columns', newColumns);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  {/* Column content based on type */}
                  {settings.columns[index]?.type === 'header' && (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={column.title || column.text || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, title: e.target.value, text: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder={settings.columns[index].default}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {renderColumnStyleControls(column, index, settings.columns[index])}
                    </div>
                  )}
                  
                  {settings.columns[index]?.type === 'text' && (
                    <div className="space-y-3">
                      <textarea
                        value={column.text || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, text: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder={settings.columns[index].default}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {renderColumnStyleControls(column, index, settings.columns[index])}
                    </div>
                  )}

                  {settings.columns[index]?.type === 'image' && (
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={column.src || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, src: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder="Image URL"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="text"
                        value={column.alt || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[index] = { ...column, alt: e.target.value };
                          updateContent('columns', newColumns);
                        }}
                        placeholder="Alt text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                </div>
              )) || null}
              
              {/* Add column button */}
              {content.columns && content.columns.length < settings.columns.length && (
                <button
                  onClick={() => {
                    const nextColumnIndex = content.columns.length;
                    const columnSettings = settings.columns[nextColumnIndex];
                    const newColumn = {
                      id: `column-${Date.now()}`,
                      type: columnSettings.type,
                      [columnSettings.type === 'header' ? 'title' : columnSettings.type === 'text' ? 'text' : 'content']: columnSettings.default || '',
                      ...columnSettings.possible_styles?.reduce((acc, style) => {
                        acc[style] = getDefaultStyleValue(style);
                        return acc;
                      }, {}) || {}
                    };
                    updateContent('columns', [...content.columns, newColumn]);
                  }}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600"
                >
                  + Add {settings.columns[content.columns?.length]?.label || 'Column'}
                </button>
              )}
            </div>
          </div>
        )}

        {renderStyleControls()}
        {renderSpacingControls()}
      </div>
    );
  };

  // Helper function for column style controls
  const renderColumnStyleControls = (column, columnIndex, columnSettings) => {
    if (!columnSettings.possible_styles) return null;

    return (
      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
        {columnSettings.possible_styles.includes('fontSize') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Font Size</label>
            <input
              type="number"
              value={column.fontSize || '16'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, fontSize: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              min="8"
              max="72"
            />
          </div>
        )}
        {columnSettings.possible_styles.includes('fontWeight') && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
            <select
              value={column.fontWeight || 'normal'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, fontWeight: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            >
              <option value="normal">Normal</option>
              <option value="medium">Medium</option>
              <option value="semibold">Semibold</option>
              <option value="bold">Bold</option>
            </select>
          </div>
        )}
        {columnSettings.possible_styles.includes('color') && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={column.color || '#1f2937'}
              onChange={(e) => {
                const newColumns = [...content.columns];
                newColumns[columnIndex] = { ...column, color: e.target.value };
                updateContent('columns', newColumns);
              }}
              className="w-full h-8 border border-gray-300 rounded cursor-pointer"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {section ? 'Edit' : 'Add'} {(allSectionTypes && allSectionTypes[sectionType]?.label) || SECTION_TYPES[sectionType]?.label || 'Section'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          {renderEditor()}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className={buttonStyles.secondary}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={buttonStyles.primary}
          >
            <Save className="w-4 h-4 mr-2" />
            {section ? 'Update' : 'Add'} Section
          </button>
        </div>
      </div>
    </div>
  );
}

// Quick Properties Panel for inline editing
function QuickPropertiesPanel({ section, onUpdate, onOpenAdvanced, allSectionTypes, selectedColumn, setSelectedColumn }) {
  console.log('QuickPropertiesPanel rendered with section:', section);
  
  if (!section) {
    return (
      <div className="bg-gray-50 border-l border-gray-200 p-4 flex-shrink-0">
        <div className="text-center py-8 text-gray-500">
          <div className="mb-3">
            <Edit className="w-8 h-8 mx-auto text-gray-300" />
          </div>
          <h3 className="text-sm font-medium text-gray-400 mb-1">Select a section</h3>
          <p className="text-xs">Click on any section to edit its properties</p>
        </div>
      </div>
    );
  }

  const content = section.content;
  
  // Get section type info with multiple fallbacks
  let sectionTypeInfo;
  if (allSectionTypes && allSectionTypes[section.section_type]) {
    sectionTypeInfo = allSectionTypes[section.section_type];
  } else if (SECTION_TYPES && SECTION_TYPES[section.section_type]) {
    sectionTypeInfo = SECTION_TYPES[section.section_type];
  } else {
    sectionTypeInfo = { 
      label: section.section_type || 'Unknown Section', 
      icon: Square 
    };
  }

  const updateProperty = (key, value) => {
    onUpdate(section.id, { [key]: value });
  };

  // Check if this is a dynamic section
  const isDynamicSection = sectionTypeInfo && sectionTypeInfo.isDynamic;

  // Helper function to render dynamic section settings
  const renderDynamicSectionSettings = () => {
    if (!isDynamicSection) return null;

    // Get the section's configuration by calling getSettings
    let sectionConfig = null;
    const getSettings = (config) => {
      sectionConfig = config;
    };

    // Load the dynamic section to get its configuration
    try {
      const dynamicSection = getDynamicSection(section.section_type);
      if (dynamicSection && dynamicSection.renderFunction) {
        // Call the section's render function just to get the configuration
        dynamicSection.renderFunction(() => ({}), content, getSettings);
      }
    } catch (error) {
      console.error('Error loading dynamic section config:', error);
      return null;
    }

    if (!sectionConfig) return null;

    const columns = content.actualColumns || [];
    
    // Find the count field (it will have a key ending with 'Count')
    const countField = sectionConfig.fields?.find(f => f.key && f.key.toLowerCase().includes('count'));

    return (
      <div className="space-y-4">
        {/* Column Count Control */}
        {countField && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              {countField.label || 'Number of Items'}
            </label>
            <select
              value={content[countField.key] || countField.default || 1}
              onChange={(e) => updateProperty(countField.key, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              {Array.from({ length: countField.max || sectionConfig.columnStructure?.maxColumns || 6 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>{num} {num === 1 ? countField.label?.replace(/Number of /, '').replace(/s$/, '') : countField.label?.replace(/Number of /, '')}</option>
              ))}
            </select>
          </div>
        )}

        {/* Column-specific settings */}
        {selectedColumn?.sectionId === section.id && selectedColumn?.columnIndex !== undefined && columns[selectedColumn.columnIndex] && sectionConfig.columnStructure && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-700">Item {selectedColumn.columnIndex + 1} Settings</h4>
            {sectionConfig.columnStructure.fields?.map(field => (
              <div key={field.key}>
                {field.type === 'text' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <input
                      type="text"
                      value={columns[selectedColumn.columnIndex][field.key] || ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: e.target.value
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      placeholder={field.default}
                    />
                  </div>
                )}
                {field.type === 'textarea' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <textarea
                      value={columns[selectedColumn.columnIndex][field.key] || ''}
                      onChange={(e) => {
                        const updatedColumns = [...columns];
                        updatedColumns[selectedColumn.columnIndex] = {
                          ...updatedColumns[selectedColumn.columnIndex],
                          [field.key]: e.target.value
                        };
                        updateProperty('actualColumns', updatedColumns);
                      }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      rows="3"
                      placeholder={field.default}
                    />
                  </div>
                )}
                {field.type === 'color' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{field.label}</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={columns[selectedColumn.columnIndex][field.key] || field.default || '#ffffff'}
                        onChange={(e) => {
                          const updatedColumns = [...columns];
                          updatedColumns[selectedColumn.columnIndex] = {
                            ...updatedColumns[selectedColumn.columnIndex],
                            [field.key]: e.target.value
                          };
                          updateProperty('actualColumns', updatedColumns);
                        }}
                        className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={columns[selectedColumn.columnIndex][field.key] || field.default || '#ffffff'}
                        onChange={(e) => {
                          const updatedColumns = [...columns];
                          updatedColumns[selectedColumn.columnIndex] = {
                            ...updatedColumns[selectedColumn.columnIndex],
                            [field.key]: e.target.value
                          };
                          updateProperty('actualColumns', updatedColumns);
                        }}
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                        placeholder={field.default}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Common controls for text-based sections
  const renderTextControls = () => {
    if (!['header', 'paragraph'].includes(section.section_type)) return null;

    return (
      <>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Font Size</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="12"
                max="72"
                value={content.fontSize || (section.section_type === 'header' ? '48' : '18')}
                onChange={(e) => updateProperty('fontSize', e.target.value)}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <input
                type="number"
                value={content.fontSize || (section.section_type === 'header' ? '48' : '18')}
                onChange={(e) => updateProperty('fontSize', e.target.value)}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded"
                min="8"
                max="120"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Text Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={content.color || '#1f2937'}
                onChange={(e) => updateProperty('color', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={content.color || '#1f2937'}
                onChange={(e) => updateProperty('color', e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                placeholder="#1f2937"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Font Weight</label>
            <select
              value={content.fontWeight || 'normal'}
              onChange={(e) => updateProperty('fontWeight', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="normal">Normal</option>
              <option value="medium">Medium</option>
              <option value="semibold">Semibold</option>
              <option value="bold">Bold</option>
            </select>
          </div>
        </div>
      </>
    );
  };

  // Button-specific controls
  const renderButtonControls = () => {
    if (section.section_type !== 'cta') return null;

    return (
      <>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Button Size</label>
            <select
              value={content.buttonSize || 'medium'}
              onChange={(e) => updateProperty('buttonSize', e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Button Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={content.backgroundColor || '#4f46e5'}
                onChange={(e) => updateProperty('backgroundColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={content.backgroundColor || '#4f46e5'}
                onChange={(e) => updateProperty('backgroundColor', e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                placeholder="#4f46e5"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Text Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={content.textColor || '#ffffff'}
                onChange={(e) => updateProperty('textColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={content.textColor || '#ffffff'}
                onChange={(e) => updateProperty('textColor', e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                placeholder="#ffffff"
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="bg-white border-l border-gray-200 flex flex-col flex-shrink-0 h-full">
      {/* Section Header */}
      <div className="flex items-center justify-between p-3 pb-2 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          {sectionTypeInfo?.icon && React.createElement(
            typeof sectionTypeInfo.icon === 'string' ? getIconComponent(sectionTypeInfo.icon) : sectionTypeInfo.icon, 
            { className: "w-3 h-3 text-gray-500" }
          )}
          <h3 className="text-sm font-medium text-gray-900">{sectionTypeInfo?.label}</h3>
        </div>
        {!isDynamicSection && (
          <button
            onClick={() => onOpenAdvanced(section)}
            className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-1.5 py-0.5"
          >
            Advanced
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-4">
        {/* Dynamic Section Settings */}
        {isDynamicSection ? renderDynamicSectionSettings() : (
          <>
            {/* Text Controls */}
            {renderTextControls()}
            
            {/* Button Controls */}
            {renderButtonControls()}
          </>
        )}

        {/* Universal Controls - Show for all sections */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Text Alignment</label>
            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              <button
                onClick={() => updateProperty('textAlign', 'left')}
                className={`flex-1 px-3 py-2 text-xs ${content.textAlign === 'left' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-700'} hover:bg-gray-50`}
              >
                Left
              </button>
              <button
                onClick={() => updateProperty('textAlign', 'center')}
                className={`flex-1 px-3 py-2 text-xs border-l border-gray-300 ${content.textAlign === 'center' || !content.textAlign ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-700'} hover:bg-gray-50`}
              >
                Center
              </button>
              <button
                onClick={() => updateProperty('textAlign', 'right')}
                className={`flex-1 px-3 py-2 text-xs border-l border-gray-300 ${content.textAlign === 'right' ? 'bg-indigo-50 text-indigo-600' : 'bg-white text-gray-700'} hover:bg-gray-50`}
              >
                Right
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Spacing</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Top (px)</label>
                <input
                  type="number"
                  value={content.marginTop || '0'}
                  onChange={(e) => updateProperty('marginTop', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  min="0"
                  max="500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Bottom (px)</label>
                <input
                  type="number"
                  value={content.marginBottom || ''}
                  onChange={(e) => updateProperty('marginBottom', e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  min="0"
                  max="500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Background Color</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={content.sectionBackgroundColor === 'transparent' ? '#ffffff' : (content.sectionBackgroundColor || '#ffffff')}
                onChange={(e) => updateProperty('sectionBackgroundColor', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={content.sectionBackgroundColor || 'transparent'}
                onChange={(e) => updateProperty('sectionBackgroundColor', e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                placeholder="transparent"
              />
              <button
                onClick={() => updateProperty('sectionBackgroundColor', 'transparent')}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

export default function ThankYouPageBuilder({ integration, onClose }) {
  const [sections, setSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null); // For quick properties panel
  const [selectedColumn, setSelectedColumn] = useState(null); // For dynamic section column selection - format: { sectionId, columnIndex }
  const [showPageSettings, setShowPageSettings] = useState(false); // For dropdown
  const [showAddSection, setShowAddSection] = useState(false); // For dropdown
  const [panelWidths, setPanelWidths] = useState({ left: 18, center: 64, right: 18 }); // Panel width percentages
  const [isResizing, setIsResizing] = useState(null); // Track which divider is being dragged
  const [allSectionTypes, setAllSectionTypes] = useState(SECTION_TYPES); // Combined static + dynamic sections
  const [pageConfig, setPageConfig] = useState({
    page_background_color: integration.config?.page_background_color || '#f9fafb',
    section_width: integration.config?.section_width || 'boxed', // boxed, full-width, custom
    custom_section_width: integration.config?.custom_section_width || '800px',
    section_max_width: integration.config?.section_max_width || '1200px'
  });
  const { showSuccess, showError } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const initializeBuilder = async () => {
      // Load dynamic sections first
      try {
        await loadDynamicSections();
        const dynamicSectionsList = getAllDynamicSections();
        
        // Combine static and dynamic sections
        const combinedSections = { 
          ...SECTION_TYPES 
        };
        
        // Add dynamic sections to the combined list
        dynamicSectionsList.forEach(section => {
          combinedSections[section.id] = {
            label: section.label,
            icon: section.icon,
            description: section.description,
            defaultContent: section.defaultContent,
            isDynamic: true,
            settings: section.settings
          };
        });
        
        setAllSectionTypes(combinedSections);
        console.log('Loaded section types:', Object.keys(combinedSections));
        
      } catch (error) {
        console.error('Failed to load dynamic sections:', error);
      }
      
      // Then fetch the existing sections
      fetchSections();
    };

    initializeBuilder();
  }, [integration.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync pageConfig with integration.config changes
  useEffect(() => {
    const newConfig = {
      page_background_color: integration.config?.page_background_color || '#f9fafb',
      section_width: integration.config?.section_width || 'boxed',
      custom_section_width: integration.config?.custom_section_width || '800px',
      section_max_width: integration.config?.section_max_width || '1200px'
    };
    console.log('Syncing pageConfig with integration.config:', newConfig);
    setPageConfig(newConfig);
  }, [integration.config]);

  // Reset selected column when selected section ID changes (not just content updates)
  useEffect(() => {
    setSelectedColumn(null);
  }, [selectedSection?.id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowPageSettings(false);
        setShowAddSection(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Panel resize handlers
  const handleMouseDown = (divider) => {
    setIsResizing(divider);
  };

  const handleMouseUp = () => {
    setIsResizing(null);
  };

  // Add mouse event listeners for panel resizing
  useEffect(() => {
    if (isResizing) {
      const handleGlobalMouseMove = (e) => {
        const mainContainer = document.querySelector('.resize-container');
        if (!mainContainer) return;
        
        const rect = mainContainer.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const totalWidth = rect.width;
        const percentage = (x / totalWidth) * 100;

        if (isResizing === 'left') {
          // Resizing between left and center panels
          const newLeft = Math.max(12, Math.min(35, percentage));
          const remaining = 100 - newLeft;
          const currentRightPercentage = (panelWidths.right / (panelWidths.center + panelWidths.right)) * remaining;
          
          setPanelWidths({
            left: newLeft,
            center: remaining - currentRightPercentage,
            right: currentRightPercentage
          });
        } else if (isResizing === 'right') {
          // Resizing between center and right panels
          const newRight = Math.max(12, Math.min(35, 100 - percentage));
          const remaining = 100 - newRight;
          const currentLeftPercentage = (panelWidths.left / (panelWidths.left + panelWidths.center)) * remaining;
          
          setPanelWidths({
            left: currentLeftPercentage,
            center: remaining - currentLeftPercentage,
            right: newRight
          });
        }
      };

      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: true });
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, panelWidths]);

  const fetchSections = async () => {
    try {
     
      const response = await apiRequest(`/integrations/thank-you-sections/${integration.id}`);
      const data = await response.json();
      setSections(data);
      
      // Auto-select first section for better UX
      if (data.length > 0 && !selectedSection) {
        setSelectedSection(data[0]);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      showError('Failed to load sections');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const activeIndex = sections.findIndex(s => s.id === active.id);
      const overIndex = sections.findIndex(s => s.id === over.id);
      
      const newSections = arrayMove(sections, activeIndex, overIndex);
      setSections(newSections);

      // Update order_index for all sections
      try {
        await Promise.all(
          newSections.map((section, index) =>
            apiRequest(`/integrations/thank-you-sections/${section.id}`, {
              method: 'PUT',
              body: JSON.stringify({ order_index: index }),
            })
          )
        );
      } catch (error) {
        console.error('Error updating section order:', error);
        showError('Failed to update section order');
        // Revert on error
        fetchSections();
      }
    }
  };

  const handleSaveSection = async (sectionData) => {
    try {
      if (sectionData.id) {
        // Update existing section
        const response = await apiRequest(`/integrations/thank-you-sections/${sectionData.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            section_type: sectionData.section_type,
            content: sectionData.content,
          }),
        });

        if (response.ok) {
          showSuccess('Section updated successfully');
          fetchSections();
        } else {
          showError('Failed to update section');
        }
      } else {
        // Create new section
        const response = await apiRequest(`/integrations/thank-you-sections/${integration.id}`, {
          method: 'POST',
          body: JSON.stringify({
            section_type: sectionData.section_type,
            content: sectionData.content,
            order_index: sections.length,
          }),
        });

        if (response.ok) {
          showSuccess('Section added successfully');
          fetchSections();
        } else {
          showError('Failed to add section');
        }
      }
    } catch (error) {
      console.error('Error saving section:', error);
      showError('Failed to save section');
    }

    setEditingSection(null);
  };

  // Add section directly without modal
  const addSectionDirectly = async (sectionType) => {
    try {
      // Get default content for the section type
      const defaultContent = allSectionTypes[sectionType]?.defaultContent || SECTION_TYPES[sectionType]?.defaultContent || {};
      
      // Handle dynamic sections
      if (allSectionTypes[sectionType]?.isDynamic) {
        const dynamicSection = getDynamicSection(sectionType);
        if (dynamicSection && dynamicSection.settings && dynamicSection.settings.columnStructure) {
          const testimonialCount = defaultContent.testimonialCount || 3;
          const defaultTestimonials = [];
          
          for (let i = 0; i < testimonialCount; i++) {
            const testimonial = {
              id: `testimonial-${Date.now()}-${i}`
            };
            
            // Add default values for each field in the column structure
            dynamicSection.settings.columnStructure.fields.forEach(field => {
              testimonial[field.key] = field.default || '';
              
              // Add style defaults based on possible_styles
              if (field.possible_styles) {
                field.possible_styles.forEach(style => {
                  testimonial[`${field.key}${style.charAt(0).toUpperCase() + style.slice(1)}`] = getDefaultStyleValue(style);
                });
              }
            });
            
            defaultTestimonials.push(testimonial);
          }
          
          defaultContent.actualColumns = defaultTestimonials;
        }
      }

      // Create new section
      const response = await apiRequest(`/integrations/thank-you-sections/${integration.id}`, {
        method: 'POST',
        body: JSON.stringify({
          section_type: sectionType,
          content: defaultContent,
          order_index: sections.length,
        }),
      });

      if (response.ok) {
        const newSection = await response.json();
        showSuccess('Section added successfully');
        fetchSections();
        
        // Auto-select the newly created section for immediate editing
        setTimeout(() => {
          setSelectedSection(newSection);
          
          // Scroll to bottom of preview panel to show the newly added section
          const previewPanel = document.querySelector('.overflow-y-auto.bg-gray-50');
          if (previewPanel) {
            previewPanel.scrollTo({
              top: previewPanel.scrollHeight,
              behavior: 'smooth'
            });
          }
        }, 200);
      } else {
        showError('Failed to add section');
      }
    } catch (error) {
      console.error('Error adding section:', error);
      showError('Failed to add section');
    }
  };

  const updatePageConfig = async (newConfig) => {
    console.log('updatePageConfig called with:', newConfig);
    // Optimistically update the local state first
    setPageConfig(prev => {
      const updated = { ...prev, ...newConfig };
      console.log('Updated pageConfig state:', updated);
      return updated;
    });

    try {
      const response = await apiRequest(`/integrations/forms/${integration.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          config: { ...integration.config, ...newConfig }
        }),
      });

      if (response.ok) {
        showSuccess('Page settings updated successfully');
        // Update the integration object if there's a way to do so
        // This ensures the parent component stays in sync
        if (integration.config) {
          Object.assign(integration.config, newConfig);
        }
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to update page settings');
        // Revert the optimistic update on failure
        setPageConfig(prev => {
          const reverted = { ...prev };
          Object.keys(newConfig).forEach(key => {
            reverted[key] = integration.config?.[key] || prev[key];
          });
          return reverted;
        });
      }
    } catch (error) {
      console.error('Error updating page config:', error);
      showError('Failed to update page settings');
      // Revert the optimistic update on failure
      setPageConfig(prev => {
        const reverted = { ...prev };
        Object.keys(newConfig).forEach(key => {
          reverted[key] = integration.config?.[key] || prev[key];
        });
        return reverted;
      });
    }
  };

  // Helper function to get section container styles based on page settings
  const getSectionContainerStyles = () => {
    const { section_width, custom_section_width, section_max_width } = pageConfig;
    
    if (section_width === 'full-width') {
      return {
        width: '100%',
        maxWidth: 'none',
        margin: '0'
      };
    } else if (section_width === 'custom') {
      return {
        width: custom_section_width || '800px',
        maxWidth: section_max_width || '1200px',
        margin: '0 auto'
      };
    } else {
      // Default boxed layout
      return {
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto'
      };
    }
  };

  // Quick update function for properties panel
  const handleQuickUpdate = async (sectionId, updates) => {
    try {
      // Find the section to update
      const sectionToUpdate = sections.find(s => s.id === sectionId);
      if (!sectionToUpdate) return;

      // Create updated section
      const updatedSection = {
        ...sectionToUpdate,
        content: { ...sectionToUpdate.content, ...updates }
      };

      // Update the API
      const response = await apiRequest(`/integrations/thank-you-sections/${sectionId}`, {
        method: 'PUT',
        body: JSON.stringify({
          section_type: updatedSection.section_type,
          content: updatedSection.content,
          order_index: updatedSection.order_index,
        }),
      });

      if (response.ok) {
        // Update local state
        setSections(prev => prev.map(s => s.id === sectionId ? updatedSection : s));
        // Update selected section if it's the one being edited
        if (selectedSection?.id === sectionId) {
          setSelectedSection(updatedSection);
        }
      } else {
        showError('Failed to update section');
      }
    } catch (error) {
      console.error('Error updating section:', error);
      showError('Failed to update section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) {
      return;
    }

    try {
      const response = await apiRequest(`/integrations/thank-you-sections/${sectionId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showSuccess('Section deleted successfully');
        fetchSections();
      } else {
        showError('Failed to delete section');
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      showError('Failed to delete section');
    }
  };

  return (
    <>
      <style>
        {`
          .section-hover-parent:hover .margin-dragger .dragger-handle {
            opacity: 0.7 !important;
          }
          .section-hover-parent .margin-dragger .dragger-handle:hover {
            opacity: 0.9 !important;
          }
          .section-hover-parent .margin-dragger .dragger-handle.opacity-100 {
            opacity: 1 !important;
          }
        `}
      </style>
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-2 ${isResizing ? 'cursor-col-resize' : ''}`}>
        <div className="bg-white rounded-xl w-full h-full max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Thank You Page Builder</h2>
              <p className="text-xs text-gray-600 mt-1">{integration.name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden resize-container">
            {/* Builder Panel */}
            <div 
              className="p-4 overflow-y-auto border-r border-gray-200"
              style={{ width: `${panelWidths.left}%` }}
            >
              <div className="space-y-3">
                {/* Compact Page Settings Dropdown */}
                <div className="relative dropdown-container">
                  <button
                    onClick={() => setShowPageSettings(!showPageSettings)}
                    className="w-full flex items-center justify-between p-2.5 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4 text-gray-600" />
                      <span className="text-xs font-medium text-gray-900">Page Settings</span>
                    </div>
                    <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${showPageSettings ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showPageSettings && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-3 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="color"
                            value={pageConfig.page_background_color || '#f9fafb'}
                            onChange={(e) => updatePageConfig({ page_background_color: e.target.value })}
                            className="w-10 h-8 border border-gray-300 rounded-md cursor-pointer"
                          />
                          <input
                            type="text"
                            value={pageConfig.page_background_color || '#f9fafb'}
                            onChange={(e) => updatePageConfig({ page_background_color: e.target.value })}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="#f9fafb"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Layout</label>
                        <select
                          value={pageConfig.section_width || 'boxed'}
                          onChange={(e) => updatePageConfig({ section_width: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="boxed">Boxed (centered container)</option>
                          <option value="full-width">Full Width</option>
                          <option value="custom">Custom Width</option>
                        </select>
                      </div>
                      
                      {pageConfig.section_width === 'custom' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Section Width</label>
                            <input
                              type="text"
                              value={pageConfig.custom_section_width || '800px'}
                              onChange={(e) => updatePageConfig({ custom_section_width: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="800px or 80%"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Max Width</label>
                            <input
                              type="text"
                              value={pageConfig.section_max_width || '1200px'}
                              onChange={(e) => updatePageConfig({ section_max_width: e.target.value })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              placeholder="1200px"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Compact Add Section Dropdown */}
                <div className="relative dropdown-container">
                  <button
                    onClick={() => setShowAddSection(!showAddSection)}
                    className="w-full flex items-center justify-between p-2.5 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Plus className="w-4 h-4 text-indigo-600" />
                      <span className="text-xs font-medium text-indigo-900">Add Section</span>
                    </div>
                    <ChevronDown className={`w-3 h-3 text-indigo-500 transition-transform ${showAddSection ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showAddSection && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-2">
                      <div className="grid grid-cols-1 gap-1">
                        {Object.entries(allSectionTypes).map(([type, info]) => {
                          const Icon = typeof info.icon === 'string' ? getIconComponent(info.icon) : info.icon;
                          
                          return (
                            <button
                              key={type}
                              onClick={() => {
                                addSectionDirectly(type);
                                setShowAddSection(false);
                              }}
                              className="flex items-center space-x-2 p-2 text-left border border-gray-200 rounded-md hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                            >
                              <Icon className="w-3 h-3 text-gray-600 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <span className="text-xs font-medium text-gray-900 block truncate">
                                  {info.label}
                                  {info.isDynamic && <span className="ml-1 px-1 py-0.5 text-xs bg-green-100 text-green-700 rounded">Dynamic</span>}
                                </span>
                                <span className="text-xs text-gray-500 truncate block">{info.description}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sections List */}
                <div>
                  <h3 className="text-xs font-medium text-gray-900 mb-2">Page Sections</h3>
                  {sections.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Type className="w-12 h-12 mx-auto mb-4" />
                      <p>No sections added yet</p>
                      <p className="text-sm">Add sections above to build your thank you page</p>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                        {sections.map((section) => (
                          <SortableSection
                            key={section.id}
                            section={section}
                            allSectionTypes={allSectionTypes}
                            renderDynamicSection={renderDynamicSection}
                            onEdit={setEditingSection}
                            onDelete={handleDeleteSection}
                            onSelect={() => setSelectedSection(section)}
                            isSelected={selectedSection?.id === section.id}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            </div>

            {/* Left Resize Handle */}
            <div 
              className={`w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors ${isResizing === 'left' ? 'bg-blue-400' : ''}`}
              onMouseDown={() => handleMouseDown('left')}
            ></div>

            {/* Preview Panel */}
            <div 
              className="p-4 overflow-y-auto bg-gray-50 border-r border-gray-200"
              style={{ width: `${panelWidths.center}%` }}
            >
                <h3 className="text-base font-medium text-gray-900 mb-3">Preview</h3>
                <div 
                  className="rounded-lg shadow-sm min-h-96"
                  style={{ backgroundColor: pageConfig.page_background_color || '#f9fafb' }}
                >
                  <div style={getSectionContainerStyles()} className="p-8">
                    {sections.map((section) => (
                      <SortableSection 
                        key={section.id} 
                        section={section} 
                        allSectionTypes={allSectionTypes}
                        renderDynamicSection={renderDynamicSection}
                        isPreview={true}
                        onSelect={() => setSelectedSection(section)}
                        isSelected={selectedSection?.id === section.id}
                        onQuickUpdate={handleQuickUpdate}
                        selectedColumn={selectedColumn}
                        setSelectedColumn={setSelectedColumn}
                      />
                    ))}
                    {sections.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">
                          {integration.config?.page_title || 'Thank You!'}
                        </h1>
                        <p className="text-xl text-gray-600">
                          Your response has been recorded successfully.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            {/* Right Resize Handle */}
            <div 
              className={`w-1 bg-gray-200 hover:bg-blue-300 cursor-col-resize transition-colors ${isResizing === 'right' ? 'bg-blue-400' : ''}`}
              onMouseDown={() => handleMouseDown('right')}
            ></div>

            {/* Properties Panel - Always visible, fixed width */}
            <div 
              className="flex-shrink-0"
              style={{ width: `${panelWidths.right}%` }}
            >
              <QuickPropertiesPanel
                section={selectedSection}
                allSectionTypes={allSectionTypes}
                onUpdate={handleQuickUpdate}
                onOpenAdvanced={(section) => setEditingSection(section)}
                selectedColumn={selectedColumn}
                setSelectedColumn={setSelectedColumn}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section Editor Modal - Only for editing existing sections */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          sectionType={editingSection.section_type}
          allSectionTypes={allSectionTypes}
          onSave={handleSaveSection}
          onCancel={() => {
            setEditingSection(null);
          }}
        />
      )}
    </>
  );
}